import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

export type CurrencyCode =
  | "AED" | "AFN" | "ALL" | "AMD" | "ANG" | "AOA" | "ARS" | "AUD" | "AWG" | "AZN"
  | "BAM" | "BBD" | "BDT" | "BGN" | "BHD" | "BIF" | "BMD" | "BND" | "BOB" | "BRL"
  | "BSD" | "BTN" | "BWP" | "BYN" | "BZD" | "CAD" | "CDF" | "CHF" | "CLP" | "CNY"
  | "COP" | "CRC" | "CUP" | "CVE" | "CZK" | "DJF" | "DKK" | "DOP" | "DZD" | "EGP"
  | "ERN" | "ETB" | "EUR" | "FJD" | "FKP" | "GBP" | "GEL" | "GHS" | "GIP" | "GMD"
  | "GNF" | "GTQ" | "GYD" | "HKD" | "HNL" | "HRK" | "HTG" | "HUF" | "IDR" | "ILS"
  | "INR" | "IQD" | "IRR" | "ISK" | "JMD" | "JOD" | "JPY" | "KES" | "KGS" | "KHR"
  | "KMF" | "KPW" | "KRW" | "KWD" | "KYD" | "KZT" | "LAK" | "LBP" | "LKR" | "LRD"
  | "LSL" | "LYD" | "MAD" | "MDL" | "MGA" | "MKD" | "MMK" | "MNT" | "MOP" | "MRU"
  | "MUR" | "MVR" | "MWK" | "MXN" | "MYR" | "MZN" | "NAD" | "NGN" | "NIO" | "NOK"
  | "NPR" | "NZD" | "OMR" | "PAB" | "PEN" | "PGK" | "PHP" | "PKR" | "PLN" | "PYG"
  | "QAR" | "RON" | "RSD" | "RUB" | "RWF" | "SAR" | "SBD" | "SCR" | "SDG" | "SEK"
  | "SGD" | "SHP" | "SLE" | "SLL" | "SOS" | "SRD" | "SSP" | "STN" | "SYP" | "SZL"
  | "THB" | "TJS" | "TMT" | "TND" | "TOP" | "TRY" | "TTD" | "TWD" | "TZS" | "UAH"
  | "UGX" | "USD" | "UYU" | "UZS" | "VES" | "VND" | "VUV" | "WST" | "XAF" | "XCD"
  | "XOF" | "XPF" | "YER" | "ZAR" | "ZMW" | "ZWL";

interface CurrencyContextType {
  currency: CurrencyCode;
  setCurrency: (currency: CurrencyCode) => void;
  formatCurrency: (amount: number, currency?: CurrencyCode) => string;
  convertCurrency: (
    amount: number,
    fromCurrency: CurrencyCode,
    toCurrency: CurrencyCode
  ) => number;
  getCurrencySymbol: (currency?: CurrencyCode) => string;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(
  undefined
);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return context;
};

interface CurrencyProviderProps {
  children: ReactNode;
}

// Currency symbols
export const currencySymbols: Record<CurrencyCode, string> = {
  AED: "د.إ", AFN: "؋", ALL: "L", AMD: "֏", ANG: "ƒ",
  AOA: "Kz", ARS: "$", AUD: "A$", AWG: "ƒ", AZN: "₼",
  BAM: "KM", BBD: "$", BDT: "৳", BGN: "лв", BHD: ".د.ب",
  BIF: "FBu", BMD: "$", BND: "$", BOB: "Bs.", BRL: "R$",
  BSD: "$", BTN: "Nu.", BWP: "P", BYN: "Br", BZD: "BZ$",
  CAD: "C$", CDF: "FC", CHF: "CHF", CLP: "$", CNY: "¥",
  COP: "$", CRC: "₡", CUP: "$", CVE: "$", CZK: "Kč",
  DJF: "Fdj", DKK: "kr", DOP: "RD$", DZD: "دج", EGP: "E£",
  ERN: "Nfk", ETB: "Br", EUR: "€", FJD: "$", FKP: "£",
  GBP: "£", GEL: "₾", GHS: "GH₵", GIP: "£", GMD: "D",
  GNF: "FG", GTQ: "Q", GYD: "$", HKD: "HK$", HNL: "L",
  HRK: "kn", HTG: "G", HUF: "Ft", IDR: "Rp", ILS: "₪",
  INR: "₹", IQD: "ع.د", IRR: "﷼", ISK: "kr", JMD: "J$",
  JOD: "د.ا", JPY: "¥", KES: "KSh", KGS: "с", KHR: "៛",
  KMF: "CF", KPW: "₩", KRW: "₩", KWD: "د.ك", KYD: "$",
  KZT: "₸", LAK: "₭", LBP: "ل.ل", LKR: "₨", LRD: "$",
  LSL: "M", LYD: "ل.د", MAD: "MAD", MDL: "L", MGA: "Ar",
  MKD: "ден", MMK: "K", MNT: "₮", MOP: "P", MRU: "UM",
  MUR: "₨", MVR: ".ރ", MWK: "MK", MXN: "$", MYR: "RM",
  MZN: "MT", NAD: "$", NGN: "₦", NIO: "C$", NOK: "kr",
  NPR: "₨", NZD: "NZ$", OMR: "﷼", PAB: "B/.", PEN: "S/.",
  PGK: "K", PHP: "₱", PKR: "₨", PLN: "zł", PYG: "Gs",
  QAR: "﷼", RON: "lei", RSD: "дин.", RUB: "₽", RWF: "RF",
  SAR: "﷼", SBD: "$", SCR: "₨", SDG: "ج.س.", SEK: "kr",
  SGD: "S$", SHP: "£", SLE: "Le", SLL: "Le", SOS: "S",
  SRD: "$", SSP: "£", STN: "Db", SYP: "£", SZL: "L",
  THB: "฿", TJS: "SM", TMT: "T", TND: "د.ت", TOP: "T$",
  TRY: "₺", TTD: "TT$", TWD: "NT$", TZS: "TSh", UAH: "₴",
  UGX: "USh", USD: "$", UYU: "$U", UZS: "so'm", VES: "Bs.S",
  VND: "₫", VUV: "VT", WST: "WS$", XAF: "FCFA", XCD: "$",
  XOF: "CFA", XPF: "₣", YER: "﷼", ZAR: "R", ZMW: "ZK",
  ZWL: "$",
};

// Exchange rates relative to USD (approximate rates)
const exchangeRates: Record<CurrencyCode, number> = {
  AED: 3.67, AFN: 70.5, ALL: 94.2, AMD: 388, ANG: 1.79, AOA: 830, ARS: 825, AUD: 1.52, AWG: 1.79, AZN: 1.7,
  BAM: 1.79, BBD: 2, BDT: 110, BGN: 1.79, BHD: 0.37, BIF: 2850, BMD: 1, BND: 1.34, BOB: 6.9, BRL: 4.95,
  BSD: 1, BTN: 83, BWP: 13.5, BYN: 3.25, BZD: 2, CAD: 1.35, CDF: 2750, CHF: 0.88, CLP: 940, CNY: 7.2,
  COP: 3950, CRC: 512, CUP: 24, CVE: 101, CZK: 23.4, DJF: 177, DKK: 6.85, DOP: 58.5, DZD: 134, EGP: 30.9,
  ERN: 15, ETB: 56, EUR: 0.92, FJD: 2.25, FKP: 0.79, GBP: 0.79, GEL: 2.65, GHS: 12.4, GIP: 0.79, GMD: 67,
  GNF: 8600, GTQ: 7.8, GYD: 209, HKD: 7.82, HNL: 24.6, HRK: 7.02, HTG: 132, HUF: 355, IDR: 15600, ILS: 3.65,
  INR: 83.1, IQD: 1310, IRR: 42000, ISK: 138, JMD: 155, JOD: 0.71, JPY: 149.5, KES: 145, KGS: 89, KHR: 4100,
  KMF: 452, KPW: 900, KRW: 1330, KWD: 0.31, KYD: 0.83, KZT: 450, LAK: 20800, LBP: 15000, LKR: 312, LRD: 190,
  LSL: 19, LYD: 4.8, MAD: 10.1, MDL: 17.8, MGA: 4500, MKD: 56.5, MMK: 2100, MNT: 3450, MOP: 8.05, MRU: 39.5,
  MUR: 45, MVR: 15.4, MWK: 1680, MXN: 17.1, MYR: 4.75, MZN: 63.8, NAD: 19, NGN: 1500, NIO: 36.6, NOK: 10.5,
  NPR: 133, NZD: 1.63, OMR: 0.38, PAB: 1, PEN: 3.75, PGK: 3.75, PHP: 56, PKR: 278, PLN: 3.98, PYG: 7280,
  QAR: 3.64, RON: 4.6, RSD: 107, RUB: 91, RWF: 1270, SAR: 3.75, SBD: 8.5, SCR: 13.5, SDG: 600, SEK: 10.3,
  SGD: 1.34, SHP: 0.79, SLE: 22.5, SLL: 22500, SOS: 570, SRD: 35, SSP: 1000, STN: 22.5, SYP: 2500, SZL: 19,
  THB: 35.8, TJS: 10.9, TMT: 3.5, TND: 3.12, TOP: 2.35, TRY: 31, TTD: 6.78, TWD: 31.5, TZS: 2530, UAH: 38,
  UGX: 3900, USD: 1, UYU: 39.2, UZS: 12400, VES: 36.2, VND: 24500, VUV: 121, WST: 2.7, XAF: 603, XCD: 2.7,
  XOF: 603, XPF: 110, YER: 250, ZAR: 19, ZMW: 25, ZWL: 322,
};

// Currency formatting options
const currencyFormatters: Record<CurrencyCode, Intl.NumberFormat> = {
  AED: new Intl.NumberFormat("ar-AE", { style: "currency", currency: "AED" }),
  AFN: new Intl.NumberFormat("ps-AF", { style: "currency", currency: "AFN" }),
  ALL: new Intl.NumberFormat("sq-AL", { style: "currency", currency: "ALL" }),
  AMD: new Intl.NumberFormat("hy-AM", { style: "currency", currency: "AMD" }),
  ANG: new Intl.NumberFormat("nl-CW", { style: "currency", currency: "ANG" }),
  AOA: new Intl.NumberFormat("pt-AO", { style: "currency", currency: "AOA" }),
  ARS: new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }),
  AUD: new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }),
  AWG: new Intl.NumberFormat("nl-AW", { style: "currency", currency: "AWG" }),
  AZN: new Intl.NumberFormat("az-AZ", { style: "currency", currency: "AZN" }),
  BAM: new Intl.NumberFormat("bs-BA", { style: "currency", currency: "BAM" }),
  BBD: new Intl.NumberFormat("en-BB", { style: "currency", currency: "BBD" }),
  BDT: new Intl.NumberFormat("bn-BD", { style: "currency", currency: "BDT" }),
  BGN: new Intl.NumberFormat("bg-BG", { style: "currency", currency: "BGN" }),
  BHD: new Intl.NumberFormat("ar-BH", { style: "currency", currency: "BHD" }),
  BIF: new Intl.NumberFormat("rn-BI", { style: "currency", currency: "BIF" }),
  BMD: new Intl.NumberFormat("en-BM", { style: "currency", currency: "BMD" }),
  BND: new Intl.NumberFormat("ms-BN", { style: "currency", currency: "BND" }),
  BOB: new Intl.NumberFormat("es-BO", { style: "currency", currency: "BOB" }),
  BRL: new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }),
  BSD: new Intl.NumberFormat("en-BS", { style: "currency", currency: "BSD" }),
  BTN: new Intl.NumberFormat("dz-BT", { style: "currency", currency: "BTN" }),
  BWP: new Intl.NumberFormat("en-BW", { style: "currency", currency: "BWP" }),
  BYN: new Intl.NumberFormat("be-BY", { style: "currency", currency: "BYN" }),
  BZD: new Intl.NumberFormat("en-BZ", { style: "currency", currency: "BZD" }),
  CAD: new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }),
  CDF: new Intl.NumberFormat("fr-CD", { style: "currency", currency: "CDF" }),
  CHF: new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF" }),
  CLP: new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP" }),
  CNY: new Intl.NumberFormat("zh-CN", { style: "currency", currency: "CNY" }),
  COP: new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP" }),
  CRC: new Intl.NumberFormat("es-CR", { style: "currency", currency: "CRC" }),
  CUP: new Intl.NumberFormat("es-CU", { style: "currency", currency: "CUP" }),
  CVE: new Intl.NumberFormat("pt-CV", { style: "currency", currency: "CVE" }),
  CZK: new Intl.NumberFormat("cs-CZ", { style: "currency", currency: "CZK" }),
  DJF: new Intl.NumberFormat("fr-DJ", { style: "currency", currency: "DJF" }),
  DKK: new Intl.NumberFormat("da-DK", { style: "currency", currency: "DKK" }),
  DOP: new Intl.NumberFormat("es-DO", { style: "currency", currency: "DOP" }),
  DZD: new Intl.NumberFormat("ar-DZ", { style: "currency", currency: "DZD" }),
  EGP: new Intl.NumberFormat("ar-EG", { style: "currency", currency: "EGP" }),
  ERN: new Intl.NumberFormat("ti-ER", { style: "currency", currency: "ERN" }),
  ETB: new Intl.NumberFormat("am-ET", { style: "currency", currency: "ETB" }),
  EUR: new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }),
  FJD: new Intl.NumberFormat("en-FJ", { style: "currency", currency: "FJD" }),
  FKP: new Intl.NumberFormat("en-FK", { style: "currency", currency: "FKP" }),
  GBP: new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }),
  GEL: new Intl.NumberFormat("ka-GE", { style: "currency", currency: "GEL" }),
  GHS: new Intl.NumberFormat("ak-GH", { style: "currency", currency: "GHS" }),
  GIP: new Intl.NumberFormat("en-GI", { style: "currency", currency: "GIP" }),
  GMD: new Intl.NumberFormat("en-GM", { style: "currency", currency: "GMD" }),
  GNF: new Intl.NumberFormat("fr-GN", { style: "currency", currency: "GNF" }),
  GTQ: new Intl.NumberFormat("es-GT", { style: "currency", currency: "GTQ" }),
  GYD: new Intl.NumberFormat("en-GY", { style: "currency", currency: "GYD" }),
  HKD: new Intl.NumberFormat("zh-HK", { style: "currency", currency: "HKD" }),
  HNL: new Intl.NumberFormat("es-HN", { style: "currency", currency: "HNL" }),
  HRK: new Intl.NumberFormat("hr-HR", { style: "currency", currency: "HRK" }),
  HTG: new Intl.NumberFormat("fr-HT", { style: "currency", currency: "HTG" }),
  HUF: new Intl.NumberFormat("hu-HU", { style: "currency", currency: "HUF" }),
  IDR: new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR" }),
  ILS: new Intl.NumberFormat("he-IL", { style: "currency", currency: "ILS" }),
  INR: new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }),
  IQD: new Intl.NumberFormat("ar-IQ", { style: "currency", currency: "IQD" }),
  IRR: new Intl.NumberFormat("fa-IR", { style: "currency", currency: "IRR" }),
  ISK: new Intl.NumberFormat("is-IS", { style: "currency", currency: "ISK" }),
  JMD: new Intl.NumberFormat("en-JM", { style: "currency", currency: "JMD" }),
  JOD: new Intl.NumberFormat("ar-JO", { style: "currency", currency: "JOD" }),
  JPY: new Intl.NumberFormat("ja-JP", { style: "currency", currency: "JPY" }),
  KES: new Intl.NumberFormat("sw-KE", { style: "currency", currency: "KES" }),
  KGS: new Intl.NumberFormat("ky-KG", { style: "currency", currency: "KGS" }),
  KHR: new Intl.NumberFormat("km-KH", { style: "currency", currency: "KHR" }),
  KMF: new Intl.NumberFormat("ar-KM", { style: "currency", currency: "KMF" }),
  KPW: new Intl.NumberFormat("ko-KP", { style: "currency", currency: "KPW" }),
  KRW: new Intl.NumberFormat("ko-KR", { style: "currency", currency: "KRW" }),
  KWD: new Intl.NumberFormat("ar-KW", { style: "currency", currency: "KWD" }),
  KYD: new Intl.NumberFormat("en-KY", { style: "currency", currency: "KYD" }),
  KZT: new Intl.NumberFormat("kk-KZ", { style: "currency", currency: "KZT" }),
  LAK: new Intl.NumberFormat("lo-LA", { style: "currency", currency: "LAK" }),
  LBP: new Intl.NumberFormat("ar-LB", { style: "currency", currency: "LBP" }),
  LKR: new Intl.NumberFormat("si-LK", { style: "currency", currency: "LKR" }),
  LRD: new Intl.NumberFormat("en-LR", { style: "currency", currency: "LRD" }),
  LSL: new Intl.NumberFormat("en-LS", { style: "currency", currency: "LSL" }),
  LYD: new Intl.NumberFormat("ar-LY", { style: "currency", currency: "LYD" }),
  MAD: new Intl.NumberFormat("ar-MA", { style: "currency", currency: "MAD" }),
  MDL: new Intl.NumberFormat("ro-MD", { style: "currency", currency: "MDL" }),
  MGA: new Intl.NumberFormat("mg-MG", { style: "currency", currency: "MGA" }),
  MKD: new Intl.NumberFormat("mk-MK", { style: "currency", currency: "MKD" }),
  MMK: new Intl.NumberFormat("my-MM", { style: "currency", currency: "MMK" }),
  MNT: new Intl.NumberFormat("mn-MN", { style: "currency", currency: "MNT" }),
  MOP: new Intl.NumberFormat("zh-MO", { style: "currency", currency: "MOP" }),
  MRU: new Intl.NumberFormat("ar-MR", { style: "currency", currency: "MRU" }),
  MUR: new Intl.NumberFormat("en-MU", { style: "currency", currency: "MUR" }),
  MVR: new Intl.NumberFormat("dv-MV", { style: "currency", currency: "MVR" }),
  MWK: new Intl.NumberFormat("en-MW", { style: "currency", currency: "MWK" }),
  MXN: new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }),
  MYR: new Intl.NumberFormat("ms-MY", { style: "currency", currency: "MYR" }),
  MZN: new Intl.NumberFormat("pt-MZ", { style: "currency", currency: "MZN" }),
  NAD: new Intl.NumberFormat("en-NA", { style: "currency", currency: "NAD" }),
  NGN: new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }),
  NIO: new Intl.NumberFormat("es-NI", { style: "currency", currency: "NIO" }),
  NOK: new Intl.NumberFormat("nb-NO", { style: "currency", currency: "NOK" }),
  NPR: new Intl.NumberFormat("ne-NP", { style: "currency", currency: "NPR" }),
  NZD: new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }),
  OMR: new Intl.NumberFormat("ar-OM", { style: "currency", currency: "OMR" }),
  PAB: new Intl.NumberFormat("es-PA", { style: "currency", currency: "PAB" }),
  PEN: new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" }),
  PGK: new Intl.NumberFormat("en-PG", { style: "currency", currency: "PGK" }),
  PHP: new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }),
  PKR: new Intl.NumberFormat("en-PK", { style: "currency", currency: "PKR" }),
  PLN: new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN" }),
  PYG: new Intl.NumberFormat("es-PY", { style: "currency", currency: "PYG" }),
  QAR: new Intl.NumberFormat("ar-QA", { style: "currency", currency: "QAR" }),
  RON: new Intl.NumberFormat("ro-RO", { style: "currency", currency: "RON" }),
  RSD: new Intl.NumberFormat("sr-RS", { style: "currency", currency: "RSD" }),
  RUB: new Intl.NumberFormat("ru-RU", { style: "currency", currency: "RUB" }),
  RWF: new Intl.NumberFormat("rw-RW", { style: "currency", currency: "RWF" }),
  SAR: new Intl.NumberFormat("ar-SA", { style: "currency", currency: "SAR" }),
  SBD: new Intl.NumberFormat("en-SB", { style: "currency", currency: "SBD" }),
  SCR: new Intl.NumberFormat("en-SC", { style: "currency", currency: "SCR" }),
  SDG: new Intl.NumberFormat("ar-SD", { style: "currency", currency: "SDG" }),
  SEK: new Intl.NumberFormat("sv-SE", { style: "currency", currency: "SEK" }),
  SGD: new Intl.NumberFormat("en-SG", { style: "currency", currency: "SGD" }),
  SHP: new Intl.NumberFormat("en-SH", { style: "currency", currency: "SHP" }),
  SLE: new Intl.NumberFormat("en-SL", { style: "currency", currency: "SLE" }),
  SLL: new Intl.NumberFormat("en-SL", { style: "currency", currency: "SLL" }),
  SOS: new Intl.NumberFormat("so-SO", { style: "currency", currency: "SOS" }),
  SRD: new Intl.NumberFormat("nl-SR", { style: "currency", currency: "SRD" }),
  SSP: new Intl.NumberFormat("en-SS", { style: "currency", currency: "SSP" }),
  STN: new Intl.NumberFormat("pt-ST", { style: "currency", currency: "STN" }),
  SYP: new Intl.NumberFormat("ar-SY", { style: "currency", currency: "SYP" }),
  SZL: new Intl.NumberFormat("en-SZ", { style: "currency", currency: "SZL" }),
  THB: new Intl.NumberFormat("th-TH", { style: "currency", currency: "THB" }),
  TJS: new Intl.NumberFormat("tg-TJ", { style: "currency", currency: "TJS" }),
  TMT: new Intl.NumberFormat("tk-TM", { style: "currency", currency: "TMT" }),
  TND: new Intl.NumberFormat("ar-TN", { style: "currency", currency: "TND" }),
  TOP: new Intl.NumberFormat("to-TO", { style: "currency", currency: "TOP" }),
  TRY: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }),
  TTD: new Intl.NumberFormat("en-TT", { style: "currency", currency: "TTD" }),
  TWD: new Intl.NumberFormat("zh-TW", { style: "currency", currency: "TWD" }),
  TZS: new Intl.NumberFormat("sw-TZ", { style: "currency", currency: "TZS" }),
  UAH: new Intl.NumberFormat("uk-UA", { style: "currency", currency: "UAH" }),
  UGX: new Intl.NumberFormat("sw-UG", { style: "currency", currency: "UGX" }),
  USD: new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }),
  UYU: new Intl.NumberFormat("es-UY", { style: "currency", currency: "UYU" }),
  UZS: new Intl.NumberFormat("uz-UZ", { style: "currency", currency: "UZS" }),
  VES: new Intl.NumberFormat("es-VE", { style: "currency", currency: "VES" }),
  VND: new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND" }),
  VUV: new Intl.NumberFormat("bi-VU", { style: "currency", currency: "VUV" }),
  WST: new Intl.NumberFormat("sm-WS", { style: "currency", currency: "WST" }),
  XAF: new Intl.NumberFormat("fr-CM", { style: "currency", currency: "XAF" }),
  XCD: new Intl.NumberFormat("en-GD", { style: "currency", currency: "XCD" }),
  XOF: new Intl.NumberFormat("fr-CI", { style: "currency", currency: "XOF" }),
  XPF: new Intl.NumberFormat("fr-PF", { style: "currency", currency: "XPF" }),
  YER: new Intl.NumberFormat("ar-YE", { style: "currency", currency: "YER" }),
  ZAR: new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR" }),
  ZMW: new Intl.NumberFormat("en-ZM", { style: "currency", currency: "ZMW" }),
  ZWL: new Intl.NumberFormat("en-ZW", { style: "currency", currency: "ZWL" }),
};

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({
  children,
}) => {
  const [currency, setCurrency] = useState<CurrencyCode>("USD");

  useEffect(() => {
    // Load saved currency preference from localStorage
    const savedCurrency = localStorage.getItem("currency") as CurrencyCode;
    if (savedCurrency && Object.keys(exchangeRates).includes(savedCurrency)) {
      setCurrency(savedCurrency);
    }
  }, []);

  // Save currency preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("currency", currency);
  }, [currency]);

  const formatCurrency = (
    amount: number,
    formatCurrency?: CurrencyCode
  ): string => {
    return currencyFormatters[formatCurrency || currency].format(amount);
  };

  const convertCurrency = (
    amount: number,
    fromCurrency: CurrencyCode,
    toCurrency: CurrencyCode
  ): number => {
    // Convert from source currency to USD, then to target currency
    const amountInUSD = amount / exchangeRates[fromCurrency];
    return amountInUSD * exchangeRates[toCurrency];
  };

  const getCurrencySymbol = (currencyCode?: CurrencyCode): string => {
    return currencySymbols[currencyCode || currency];
  };

  const value = {
    currency,
    setCurrency,
    formatCurrency,
    convertCurrency,
    getCurrencySymbol,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
